import { collection, doc, getDoc, getDocs, getFirestore, query, runTransaction, serverTimestamp, where } from 'firebase/firestore'
import { getFirebaseApp } from '../../config/firebase'
import { stringValue, timestampValue } from './recordHelpers'
import { legacyNotificationStatusForDevice, shouldWriteLegacyNotificationStatus } from '../../features/notifications/notificationCompatibility'
import type { NotificationStatusValue, NotificationTokenRecord, PushTokenStatus } from './firestoreTypes'

export function notificationDeviceDocumentId(uid: string, deviceId: string): string {
  return `${uid}__${deviceId}`
}

function mapNotificationToken(snapshot: { id: string; data: () => Record<string, unknown> }): NotificationTokenRecord {
  const data = snapshot.data()
  const status = data.status === 'active' || data.status === 'revoked' || data.status === 'error' ? data.status : 'error'
  const browser = stringValue(data.browser) || stringValue(data.browser_name)
  const userAgent = stringValue(data.userAgent) || stringValue(data.user_agent)
  const createdAt = timestampValue(data.createdAt ?? data.created_at)
  const updatedAt = timestampValue(data.updatedAt ?? data.updated_at)
  return {
    id: snapshot.id,
    deviceId: stringValue(data.deviceId) || snapshot.id,
    uid: stringValue(data.uid),
    schoolId: stringValue(data.schoolId),
    role: stringValue(data.role),
    token: stringValue(data.token),
    status,
    onboardingStatus: data.onboardingStatus === 'enabled' || data.onboardingStatus === 'denied'
      || data.onboardingStatus === 'skipped' || data.onboardingStatus === 'unsupported' || data.onboardingStatus === 'unknown'
      ? data.onboardingStatus
      : undefined,
    browser,
    platform: stringValue(data.platform),
    userAgent,
    lastSeenAt: timestampValue(data.lastSeenAt),
    createdAt,
    updatedAt,
    errorMessage: stringValue(data.errorMessage) || stringValue(data.error_message),
    browser_name: browser,
    user_agent: userAgent,
    created_at: createdAt,
    updated_at: updatedAt,
    error_message: stringValue(data.errorMessage) || stringValue(data.error_message),
  }
}

export async function getOwnNotificationToken(uid: string, deviceId: string): Promise<NotificationTokenRecord | null> {
  const firestore = getFirestore(getFirebaseApp())
  const snapshot = await getDoc(doc(firestore, 'notification_tokens', notificationDeviceDocumentId(uid, deviceId)))
  return snapshot.exists() ? mapNotificationToken(snapshot) : null
}

export async function saveOwnNotificationToken(input: {
  deviceId: string
  uid: string
  schoolId: string
  role: string
  token: string
  status: PushTokenStatus
  onboardingStatus: NotificationStatusValue
  browser: string
  platform: string
  userAgent: string
  errorMessage?: string
}): Promise<void> {
  const firestore = getFirestore(getFirebaseApp())
  const tokenRef = doc(firestore, 'notification_tokens', notificationDeviceDocumentId(input.uid, input.deviceId))
  const statusRef = doc(firestore, 'notification_status', input.uid)

  await runTransaction(firestore, async (transaction) => {
    const existing = await transaction.get(tokenRef)
    const fields = {
      deviceId: input.deviceId,
      uid: input.uid,
      schoolId: input.schoolId,
      role: input.role,
      token: input.token,
      status: input.status,
      onboardingStatus: input.onboardingStatus,
      browser: input.browser,
      platform: input.platform,
      userAgent: input.userAgent,
      lastSeenAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      errorMessage: input.errorMessage ?? '',
    }
    if (existing.exists()) {
      transaction.update(tokenRef, fields)
    } else {
      transaction.set(tokenRef, { ...fields, createdAt: serverTimestamp() })
    }
    if (shouldWriteLegacyNotificationStatus(input.role)) {
      transaction.set(statusRef, {
        uid: input.uid,
        schoolId: input.schoolId,
        role: input.role,
        ...legacyNotificationStatusForDevice(input),
        fcm_updated_at: serverTimestamp(),
        last_checked_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      }, { merge: true })
    }
  })
}

export async function saveOwnNotificationTokenError(input: {
  deviceId: string
  uid: string
  schoolId: string
  role: string
  onboardingStatus: NotificationStatusValue
  errorMessage: string
  browser: string
  platform: string
  userAgent: string
}): Promise<void> {
  await saveOwnNotificationToken({
    ...input,
    token: '',
    status: 'error',
  })
}

export async function listNotificationTokens(schoolId: string): Promise<NotificationTokenRecord[]> {
  const firestore = getFirestore(getFirebaseApp())
  const result = await getDocs(query(collection(firestore, 'notification_tokens'), where('schoolId', '==', schoolId)))
  return result.docs
    .map(mapNotificationToken)
    .sort((a, b) => a.role.localeCompare(b.role) || a.uid.localeCompare(b.uid) || a.deviceId.localeCompare(b.deviceId))
}
